(() => {
  if (window.RimalVariantGallery) return;

  const STALE_SLIDE_CLASSES = [
    'swiper-slide-active',
    'swiper-slide-prev',
    'swiper-slide-next',
    'swiper-slide-visible',
    'swiper-slide-fully-visible',
    'swiper-slide-thumb-active',
    'swiper-slide-duplicate-active',
    'swiper-slide-duplicate-prev',
    'swiper-slide-duplicate-next'
  ];

  const normalizeMediaId = (value) => {
    const match = String(value ?? '').trim().match(/(\d+)\s*$/);
    return match ? match[1] : '';
  };

  const parseMediaIds = (value) => {
    const list = Array.isArray(value) ? value : String(value ?? '').split(/[|,;\s]+/);
    return list.map(normalizeMediaId).filter(Boolean);
  };

  const mediaIdOf = (element) => {
    if (!element) return '';
    const nested = element.querySelector('[data-media-id]');
    return normalizeMediaId(
      element.dataset.rimalMediaId || element.dataset.mediaId || (nested && nested.dataset.mediaId)
    );
  };

  const isLiveSwiper = (swiper) => Boolean(swiper && !swiper.destroyed && swiper.wrapperEl);

  class GalleryController {
    constructor(gallery) {
      this.gallery = gallery;
      this.wiredSliders = new WeakSet();
      this.knownSlider = null;
      this.knownNavSlider = null;
      this.state = null;

      const mainItems = Array.from(gallery.querySelectorAll('.sf-prod-media-item')).filter(
        (item) => !item.closest('.nav-swiper-container') && !item.classList.contains('swiper-slide-duplicate')
      );
      const navItems = Array.from(
        gallery.querySelectorAll('.nav-swiper-container .swiper-wrapper > .swiper-slide')
      ).filter((item) => !item.classList.contains('swiper-slide-duplicate'));

      this.main = { wrapper: mainItems[0] ? mainItems[0].parentElement : null, items: mainItems };
      this.nav = { wrapper: navItems[0] ? navItems[0].parentElement : null, items: navItems };
      this.mainIds = mainItems.map(mediaIdOf);
    }

    apply(allowedIds, featuredId) {
      const allowed = allowedIds && allowedIds.length ? new Set(allowedIds) : null;
      let keepIndexes = this.main.items.map((_, index) => index);

      if (allowed) {
        const filtered = keepIndexes.filter((index) => allowed.has(this.mainIds[index]));
        if (filtered.length) keepIndexes = filtered;
      }

      const keepMain = keepIndexes.map((index) => this.main.items[index]);
      const keepNav = keepIndexes.map((index) => this.nav.items[index]).filter(Boolean);
      const startIndex = Math.max(
        0,
        keepIndexes.findIndex((index) => this.mainIds[index] === featuredId)
      );
      const signature = keepIndexes.join(',');

      const unchanged =
        this.state &&
        this.state.signature === signature &&
        this.knownSlider === this.gallery.slider &&
        this.knownNavSlider === this.gallery.navSlider;

      this.state = { signature, startIndex, keepMain, keepNav };

      if (!unchanged) {
        this.rebuild(this.main, keepMain, this.gallery.slider);
        this.rebuild(this.nav, keepNav, this.gallery.navSlider);
        this.knownSlider = this.gallery.slider;
        this.knownNavSlider = this.gallery.navSlider;
        this.refreshLightbox(keepMain);
      }

      this.activate(startIndex);
    }

    rebuild(part, keep, swiper) {
      if (!part.wrapper) return;

      keep.forEach((item, index) => {
        item.removeAttribute('data-rimal-vg-hidden');
        item.dataset.index = index;
        STALE_SLIDE_CLASSES.forEach((className) => item.classList.remove(className));

        if (this.gallery.layout === 'layout-2' && item.classList.contains('sf-prod-media-item')) {
          item.classList.toggle('col-span-2', index % 3 === 0 || keep.length < 3);
        }
      });

      part.wrapper.replaceChildren(...keep);
      if (isLiveSwiper(swiper)) swiper.update();
    }

    activate(index) {
      const { slider, navSlider } = this.gallery;
      const keepNav = this.state ? this.state.keepNav : [];

      keepNav.forEach((item, itemIndex) => {
        item.classList.toggle('swiper-slide-thumb-active', itemIndex === index);
      });

      if (isLiveSwiper(navSlider)) {
        navSlider.update();
        navSlider.slideTo(index, 0);
      }

      if (isLiveSwiper(slider)) {
        this.wireSlider(slider);
        slider.update();
        if (slider.activeIndex !== index) {
          slider.slideTo(index, 0);
        } else {
          slider.emit('slideChange');
        }
        if (slider.thumbs && typeof slider.thumbs.update === 'function') slider.thumbs.update(true);
        if (typeof slider.updateAutoHeight === 'function') slider.updateAutoHeight(0);
      }
    }

    wireSlider(slider) {
      if (this.wiredSliders.has(slider)) return;
      this.wiredSliders.add(slider);
      if (slider === this.gallery.slider && typeof this.gallery.handleSlideChange === 'function') {
        this.gallery.handleSlideChange();
      }
    }

    refreshLightbox(keepMain) {
      const { gallery } = this;
      if (!gallery.enableImageZoom || !gallery.productData || !Array.isArray(gallery.productData.media)) return;
      if (typeof gallery.initLightbox !== 'function') return;

      const order = keepMain.map(mediaIdOf);
      const media = gallery.productData.media
        .filter((item) => order.includes(String(item.id)))
        .sort((a, b) => order.indexOf(String(a.id)) - order.indexOf(String(b.id)));

      gallery.initLightbox(media);

      if (!gallery.dataset.rimalLightboxWired && typeof gallery.addEventToMainMedias === 'function') {
        gallery.dataset.rimalLightboxWired = 'true';
        gallery.addEventToMainMedias();
      }
    }

    syncSliders() {
      const sliderChanged =
        this.gallery.slider !== this.knownSlider || this.gallery.navSlider !== this.knownNavSlider;
      if (!sliderChanged || !this.state) return false;

      this.state.signature = null;
      return true;
    }
  }

  class SectionController {
    constructor(section, data) {
      this.section = section;
      this.variants = data.variants || {};
      this.productId = String(data.productId || '');
      this.filtered = false;
      this.currentVariantId = '';

      this.galleries = Array.from(section.querySelectorAll('media-gallery[data-rimal-variant-gallery]')).map(
        (gallery) => new GalleryController(gallery)
      );

      this.variantInput =
        section.querySelector('form.main-product-form [name="id"]') ||
        section.querySelector('form[action*="/cart/add"] [name="id"]');

      if (!this.galleries.length || !this.variantInput) return;

      this.applyVariant(this.variantInput.value, true);
      this.bindEvents();
      this.watchSliders();
    }

    getEntry(variantId) {
      return this.variants[String(variantId)] || null;
    }

    applyVariant(variantId, force = false) {
      const id = String(variantId || '');
      if (!id) return;
      if (!force && id === this.currentVariantId && !this.galleries.some((g) => g.syncSliders())) return;

      this.currentVariantId = id;
      const entry = this.getEntry(id);
      const allowed = entry ? parseMediaIds(entry.galleryMediaIds) : [];
      const featured = normalizeMediaId(entry && entry.featuredMediaId) || allowed[0] || '';

      if (!allowed.length && !this.filtered) return;

      this.filtered = allowed.length > 0;
      this.galleries.forEach((gallery) => gallery.apply(allowed, featured));
    }

    reapply() {
      const id = this.currentVariantId || this.variantInput.value;
      this.currentVariantId = '';
      this.applyVariant(id, true);
    }

    bindEvents() {
      this.variantInput.addEventListener('change', () => this.applyVariant(this.variantInput.value));

      if (window.MinimogEvents && typeof window.MinimogEvents.subscribe === 'function' && this.productId) {
        window.MinimogEvents.subscribe(`${this.productId}__VARIANT_CHANGE`, (variant) => {
          if (variant && variant.id) this.applyVariant(variant.id);
        });
      }

      const onBreakpoint = () => setTimeout(() => this.reapply(), 0);
      document.addEventListener('matchMobile', onBreakpoint);
      document.addEventListener('unmatchMobile', onBreakpoint);

      window.addEventListener('popstate', () => this.syncFromUrl());
      window.addEventListener('pageshow', (event) => {
        if (event.persisted) this.syncFromUrl();
      });
    }

    watchSliders() {
      const interval = 150;
      const maxWait = 15000;
      let elapsed = 0;

      const isReady = ({ gallery }) => {
        const isMobile = Boolean(window.MinimogTheme && MinimogTheme.config && MinimogTheme.config.mqlMobile);
        const activeScreen = isMobile ? 'mobile' : 'desktop';
        const usesSlider = Boolean(gallery.querySelector('.swiper-container'));
        return gallery.dataset.screen !== activeScreen || !usesSlider || isLiveSwiper(gallery.slider);
      };

      const timer = setInterval(() => {
        elapsed += interval;
        if (this.galleries.some((gallery) => gallery.syncSliders())) this.reapply();
        if (elapsed >= maxWait || this.galleries.every(isReady)) clearInterval(timer);
      }, interval);
    }

    syncFromUrl() {
      const urlVariantId = new URL(window.location.href).searchParams.get('variant');
      const targetId = urlVariantId || this.variantInput.value;
      if (urlVariantId && urlVariantId !== this.variantInput.value) {
        if (this.selectVariant(urlVariantId)) return;
      }
      this.currentVariantId = '';
      this.applyVariant(targetId, true);
    }

    selectVariant(variantId) {
      const variantsNode = this.section.querySelector('#productVariants[type="application/json"]');
      const picker = this.section.querySelector('variant-picker');
      if (!variantsNode || !picker) return false;

      let variants = [];
      try {
        variants = JSON.parse(variantsNode.textContent);
      } catch (error) {
        return false;
      }

      const variant = variants.find((item) => String(item.id) === String(variantId));
      if (!variant) return false;

      const fields = Array.from(picker.querySelectorAll('[data-picker-field]'));
      let lastChanged = null;

      fields.forEach((field, index) => {
        const value = variant.options[index];
        if (value === undefined) return;

        if (field.dataset.pickerField === 'radio') {
          const radio = Array.from(field.querySelectorAll('input[type="radio"]')).find((input) => input.value === value);
          if (radio && !radio.checked) {
            radio.checked = true;
            lastChanged = radio;
          }
        } else {
          const select = field.querySelector('select');
          if (select && select.value !== value) {
            select.value = value;
            lastChanged = select;
          }
        }
      });

      if (!lastChanged) return false;
      lastChanged.dispatchEvent(new Event('change', { bubbles: true }));
      return true;
    }
  }

  const controllers = new Map();

  const init = (root = document) => {
    root.querySelectorAll('script[type="application/json"][data-rimal-variant-gallery]').forEach((node) => {
      const sectionId = node.dataset.sectionId;
      const section = document.getElementById(`shopify-section-${sectionId}`) || node.parentElement;
      if (!section || controllers.has(section)) return;

      let data;
      try {
        data = JSON.parse(node.textContent);
      } catch (error) {
        console.error('[RimalVariantGallery] Invalid variant gallery data', error);
        return;
      }

      controllers.set(section, new SectionController(section, data));
    });
  };

  window.RimalVariantGallery = { init, normalizeMediaId, parseMediaIds };

  init();

  document.addEventListener('shopify:section:load', (event) => {
    const section = event.target;
    controllers.forEach((_, key) => {
      if (!key.isConnected) controllers.delete(key);
    });
    init(section);
  });
})();
